/**
 * Session manager hook.
 *
 * Manages session data per gateway:
 * - Fetches agents, sessions, and models when a gateway connects
 * - Provides methods to patch, reset, and switch sessions
 * - Tracks the active session key per gateway
 * - Refreshes session data on demand
 *
 * Multi-gateway aware: each gateway has its own session data.
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import type { WSClient } from '../ws/client';
import type { EventFrame, ChatEventPayload, AgentEventPayload } from '../types/protocol';
import type { GatewayConfig, GatewayStatus } from '../types/gateway';
import type {
  GatewaySessionData,
  SessionRow,
  SessionPatchParams,
  SessionCreateParams,
  GatewayAgent,
  ModelCatalogEntry,
  SessionsDefaults,
} from '../types/sessions';
import {
  listAgents,
  listSessions,
  listModels,
  patchSession,
  createSession,
  deleteSession,
  resetSession,
  patchAgent,
} from '../chat/sessions';
import { getInitialSessionKey } from '../utils/constants';

// ── Types ──

export interface UseSessionManagerReturn {
  /** Session data for the active gateway */
  activeSessionData: GatewaySessionData | null;
  /** All session data keyed by gateway ID */
  allSessionData: Map<string, GatewaySessionData>;
  /** The active session key for the current gateway */
  activeSessionKey: string;
  /** Sessions grouped by agent for the active gateway */
  sessionsByAgent: Map<string, SessionRow[]>;
  /** Agents list for the active gateway */
  agents: GatewayAgent[];
  /** Available models for the active gateway */
  models: ModelCatalogEntry[];
  /** Session defaults for the active gateway */
  defaults: SessionsDefaults | null;
  /** Whether data is currently loading */
  loading: boolean;
  /** Fetch/refresh session data for a gateway */
  fetchSessionData: (gatewayId: string) => Promise<void>;
  /** Refresh sessions only (lighter-weight) for a gateway — call on live activity */
  refreshSessionsForGateway: (gatewayId: string) => Promise<void>;
  /** Refresh data for all connected gateways */
  refreshAll: () => Promise<void>;
  /** Switch the active session (for the focused gateway) */
  switchSession: (sessionKey: string) => void;
  /** Patch a session's properties */
  patchSessionConfig: (params: SessionPatchParams) => Promise<boolean>;
  /** Create a fresh session without replacing an existing one */
  createSessionData: (params: SessionCreateParams) => Promise<string | null>;
  /** Archive/remove a session from the overview */
  deleteSessionData: (key: string) => Promise<boolean>;
  /** Patch an agent's default model */
  patchAgentModel: (agentId: string, model: string | null) => Promise<boolean>;
  /** Reset a session */
  resetSessionData: (key: string) => Promise<boolean>;
  /** Find a session by key */
  findSession: (key: string) => SessionRow | undefined;
}

const EMPTY_SESSION_DATA: GatewaySessionData = {
  agents: [],
  sessions: [],
  models: [],
  defaults: null,
  defaultAgentId: 'main',
  loading: false,
  error: null,
  lastFetched: null,
};

const SESSION_ACTIVITY_REFRESH_THROTTLE_MS = 1500;

interface GatewaySessionRefreshState {
  timer: ReturnType<typeof setTimeout>;
  trailing: boolean;
  lastRefreshAt: number;
}

function nextGatewaySequence(
  sequences: Map<string, number>,
  gatewayId: string,
): number {
  const nextSequence = (sequences.get(gatewayId) ?? 0) + 1;
  sequences.set(gatewayId, nextSequence);
  return nextSequence;
}

function invalidateGatewaySequence(
  sequences: Map<string, number>,
  gatewayId: string,
): void {
  nextGatewaySequence(sequences, gatewayId);
}

export function frameHasSessionActivity(frame: EventFrame): boolean {
  if (frame.event === 'chat') {
    const payload = frame.payload as ChatEventPayload;
    return 'sessionKey' in payload && typeof payload.sessionKey === 'string' && payload.sessionKey.length > 0;
  }

  if (frame.event === 'agent') {
    const payload = frame.payload as AgentEventPayload;
    return typeof payload.sessionKey === 'string' && payload.sessionKey.length > 0;
  }

  return false;
}

function parseAgentIdFromSessionKey(sessionKey: string): string | null {
  const parts = sessionKey.split(':');
  if (parts.length < 2 || parts[0] !== 'agent') return null;
  return parts[1] ?? null;
}

function buildMainSessionKey(agentId: string): string {
  return `agent:${agentId}:main`;
}

function hasAgent(agents: GatewayAgent[], agentId: string): boolean {
  return agents.some((agent) => agent.id === agentId);
}

function chooseFallbackSessionKey(
  sessions: SessionRow[],
  defaultAgentId: string,
  currentSessionKey?: string,
): string | null {
  if (sessions.length === 0) return null;

  const preferredAgentId = currentSessionKey ? parseAgentIdFromSessionKey(currentSessionKey) : null;
  if (preferredAgentId) {
    const preferredMain = sessions.find((session) => session.key === buildMainSessionKey(preferredAgentId));
    if (preferredMain) return preferredMain.key;

    const preferredSession = [...sessions]
      .filter((session) => parseAgentIdFromSessionKey(session.key) === preferredAgentId)
      .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))[0];
    if (preferredSession) return preferredSession.key;
  }

  const defaultMain = sessions.find((session) => session.key === buildMainSessionKey(defaultAgentId));
  if (defaultMain) return defaultMain.key;

  return [...sessions]
    .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))[0]?.key ?? null;
}

function choosePostDeleteFallbackSessionKey(
  sessions: SessionRow[],
  defaultAgentId: string,
  deletedSessionKey: string,
): string | null {
  const preferred = chooseFallbackSessionKey(sessions, defaultAgentId, deletedSessionKey);
  if (preferred) return preferred;

  const deletedAgentId = parseAgentIdFromSessionKey(deletedSessionKey);
  if (deletedAgentId) {
    return buildMainSessionKey(deletedAgentId);
  }

  return buildMainSessionKey(defaultAgentId);
}

function armGatewaySessionRefreshTimer(
  refreshStates: Map<string, GatewaySessionRefreshState>,
  gatewayId: string,
  refreshSessionsForGateway: (gatewayId: string) => void | Promise<void>,
  throttleMs: number,
): ReturnType<typeof setTimeout> {
  return setTimeout(() => {
    const state = refreshStates.get(gatewayId);
    if (!state) return;

    if (!state.trailing) {
      refreshStates.delete(gatewayId);
      return;
    }

    state.trailing = false;
    state.lastRefreshAt = Date.now();
    void refreshSessionsForGateway(gatewayId);
    state.timer = armGatewaySessionRefreshTimer(
      refreshStates,
      gatewayId,
      refreshSessionsForGateway,
      throttleMs,
    );
  }, throttleMs);
}

export function throttleGatewaySessionRefresh(
  refreshStates: Map<string, GatewaySessionRefreshState>,
  gatewayId: string,
  refreshSessionsForGateway: (gatewayId: string) => void | Promise<void>,
  throttleMs = SESSION_ACTIVITY_REFRESH_THROTTLE_MS,
): void {
  const now = Date.now();
  const existing = refreshStates.get(gatewayId);

  if (!existing || now - existing.lastRefreshAt >= throttleMs) {
    if (existing) {
      clearTimeout(existing.timer);
    }

    void refreshSessionsForGateway(gatewayId);
    refreshStates.set(gatewayId, {
      timer: armGatewaySessionRefreshTimer(
        refreshStates,
        gatewayId,
        refreshSessionsForGateway,
        throttleMs,
      ),
      trailing: false,
      lastRefreshAt: now,
    });
    return;
  }

  existing.trailing = true;
}

/**
 * Hook for managing sessions across all connected gateways.
 */
export function useSessionManager(params: {
  gateways: GatewayConfig[];
  activeGatewayId: string | null;
  connectionStates: Map<string, GatewayStatus>;
  getClient: (gatewayId: string) => WSClient | null;
  onSessionSwitch?: (gatewayId: string, sessionKey: string) => void;
}): UseSessionManagerReturn {
  const { gateways, activeGatewayId, connectionStates, getClient, onSessionSwitch } = params;

  // Session data per gateway
  const [sessionDataMap, setSessionDataMap] = useState<Map<string, GatewaySessionData>>(new Map());

  // Active session key per gateway
  const [sessionKeys, setSessionKeys] = useState<Map<string, string>>(new Map());

  // Track which gateways we've fetched for to auto-fetch on connect
  const fetchedRef = useRef<Set<string>>(new Set());

  // Per-gateway websocket listeners + throttled refresh state for event-driven session refresh
  const activityListenersRef = useRef<Map<string, (frame: EventFrame) => void>>(new Map());
  const activityClientsRef = useRef<Map<string, WSClient>>(new Map());
  const activityRefreshStatesRef = useRef<Map<string, GatewaySessionRefreshState>>(new Map());
  const sessionSnapshotSequencesRef = useRef<Map<string, number>>(new Map());
  const fullFetchSequencesRef = useRef<Map<string, number>>(new Map());
  const pendingCreatedSessionKeysRef = useRef<Map<string, string>>(new Map());

  // ── Fetch session data for a gateway ──
  const fetchSessionData = useCallback(async (gatewayId: string) => {
    const client = getClient(gatewayId);
    if (!client) return;

    const fullFetchSequence = nextGatewaySequence(fullFetchSequencesRef.current, gatewayId);
    const sessionSnapshotSequence = nextGatewaySequence(
      sessionSnapshotSequencesRef.current,
      gatewayId,
    );

    // Mark as loading
    setSessionDataMap(prev => {
      const next = new Map(prev);
      const existing = next.get(gatewayId) ?? { ...EMPTY_SESSION_DATA };
      next.set(gatewayId, { ...existing, loading: true, error: null });
      return next;
    });

    try {
      // Fetch agents, sessions, and models in parallel
      const [agentsResult, initialSessionsResult, modelsResult] = await Promise.all([
        listAgents(client).catch(err => {
          console.warn(`[SessionManager] Failed to list agents for ${gatewayId}:`, err);
          return null;
        }),
        listSessions(client).catch(err => {
          console.warn(`[SessionManager] Failed to list sessions for ${gatewayId}:`, err);
          return null;
        }),
        listModels(client).catch(err => {
          console.warn(`[SessionManager] Failed to list models for ${gatewayId}:`, err);
          return null;
        }),
      ]);

      let sessionsResult = initialSessionsResult;
      const defaultAgentId = agentsResult?.defaultId ?? 'main';
      const defaultMainSessionKey = buildMainSessionKey(defaultAgentId);
      if (
        agentsResult
        && sessionsResult
        && hasAgent(agentsResult.agents, defaultAgentId)
        && !sessionsResult.sessions.some((session) => session.key === defaultMainSessionKey)
      ) {
        try {
          const created = await createSession(client, { agentId: defaultAgentId, key: 'main' });
          if (created?.ok) {
            sessionsResult = await listSessions(client, {
              includeDerivedTitles: true,
              includeLastMessage: true,
            });
          }
        } catch (err) {
          console.warn(`[SessionManager] Failed to ensure default main session for ${gatewayId}:`, err);
        }
      }

      const isLatestFullFetch = fullFetchSequencesRef.current.get(gatewayId) === fullFetchSequence;
      const isLatestSessionSnapshot =
        sessionSnapshotSequencesRef.current.get(gatewayId) === sessionSnapshotSequence;

      if (!isLatestFullFetch && !isLatestSessionSnapshot) {
        return;
      }

      const pendingCreatedKey = pendingCreatedSessionKeysRef.current.get(gatewayId);
      if (pendingCreatedKey && sessionsResult?.sessions?.some((session) => session.key === pendingCreatedKey)) {
        pendingCreatedSessionKeysRef.current.delete(gatewayId);
      }

      setSessionDataMap(prev => {
        const next = new Map(prev);
        const existing = next.get(gatewayId) ?? { ...EMPTY_SESSION_DATA };
        const nextState: GatewaySessionData = {
          ...existing,
        };

        if (isLatestFullFetch) {
          nextState.agents = agentsResult?.agents ?? [];
          nextState.models = modelsResult?.models ?? [];
          nextState.defaultAgentId = agentsResult?.defaultId ?? 'main';
          nextState.loading = false;
          nextState.error = null;
        }

        if (isLatestSessionSnapshot) {
          nextState.sessions = sessionsResult?.sessions ?? [];
          nextState.defaults = sessionsResult?.defaults ?? null;
          nextState.lastFetched = Date.now();
        }

        next.set(gatewayId, nextState);
        return next;
      });

      fetchedRef.current.add(gatewayId);
    } catch (err) {
      console.error(`[SessionManager] Failed to fetch session data for ${gatewayId}:`, err);

      if (fullFetchSequencesRef.current.get(gatewayId) !== fullFetchSequence) {
        return;
      }

      setSessionDataMap(prev => {
        const next = new Map(prev);
        const existing = next.get(gatewayId) ?? { ...EMPTY_SESSION_DATA };
        next.set(gatewayId, {
          ...existing,
          loading: false,
          error: err instanceof Error ? err.message : 'Unknown error',
        });
        return next;
      });
    }
  }, [getClient]);

  // ── Refresh sessions only (lighter-weight than full fetch) ──
  const refreshSessionsForGateway = useCallback(async (gatewayId: string) => {
    const client = getClient(gatewayId);
    if (!client) return;

    const sessionSnapshotSequence = nextGatewaySequence(
      sessionSnapshotSequencesRef.current,
      gatewayId,
    );

    try {
      const result = await listSessions(client, {
        includeDerivedTitles: true,
        includeLastMessage: true,
      });

      if (sessionSnapshotSequencesRef.current.get(gatewayId) !== sessionSnapshotSequence) {
        return;
      }

      const pendingCreatedKey = pendingCreatedSessionKeysRef.current.get(gatewayId);
      if (pendingCreatedKey && result.sessions?.some((session) => session.key === pendingCreatedKey)) {
        pendingCreatedSessionKeysRef.current.delete(gatewayId);
      }

      setSessionDataMap((prev) => {
        const next = new Map(prev);
        const existing = next.get(gatewayId) ?? { ...EMPTY_SESSION_DATA };
        next.set(gatewayId, {
          ...existing,
          sessions: result.sessions ?? [],
          defaults: result.defaults ?? existing.defaults,
          lastFetched: Date.now(),
        });
        return next;
      });
    } catch (err) {
      console.warn(`[SessionManager] Failed to refresh sessions for ${gatewayId}:`, err);
    }
  }, [getClient]);

  const scheduleRefreshSessionsForGateway = useCallback((gatewayId: string) => {
    throttleGatewaySessionRefresh(
      activityRefreshStatesRef.current,
      gatewayId,
      refreshSessionsForGateway,
    );
  }, [refreshSessionsForGateway]);

  // ── Clear fetch tracking when a gateway disconnects ──
  // So that when it reconnects, the auto-fetch effect will re-fetch fresh data.
  useEffect(() => {
    for (const gw of gateways) {
      const status = connectionStates.get(gw.id);
      if (status !== 'connected') {
        fetchedRef.current.delete(gw.id);
      }
    }
  }, [gateways, connectionStates]);

  // ── Auto-fetch on gateway connect ──
  useEffect(() => {
    for (const gw of gateways) {
      const status = connectionStates.get(gw.id);
      if (status === 'connected' && !fetchedRef.current.has(gw.id)) {
        void fetchSessionData(gw.id);
      }
    }
  }, [gateways, connectionStates, fetchSessionData]);

  // ── Event-driven session refresh from websocket activity ──
  useEffect(() => {
    const listeners = activityListenersRef.current;
    const clients = activityClientsRef.current;
    const refreshStates = activityRefreshStatesRef.current;

    const cleanupGateway = (gatewayId: string) => {
      const refreshState = refreshStates.get(gatewayId);
      if (refreshState) {
        clearTimeout(refreshState.timer);
        refreshStates.delete(gatewayId);
      }

      invalidateGatewaySequence(sessionSnapshotSequencesRef.current, gatewayId);
      invalidateGatewaySequence(fullFetchSequencesRef.current, gatewayId);
      pendingCreatedSessionKeysRef.current.delete(gatewayId);

      const client = clients.get(gatewayId);
      const listener = listeners.get(gatewayId);
      if (client && listener) {
        client.off('event', listener);
      }

      clients.delete(gatewayId);
      listeners.delete(gatewayId);
    };

    for (const gw of gateways) {
      const status = connectionStates.get(gw.id);
      const client = getClient(gw.id);
      const existingClient = clients.get(gw.id);

      if (status === 'connected' && client) {
        if (!listeners.has(gw.id) || existingClient !== client) {
          if (listeners.has(gw.id)) {
            cleanupGateway(gw.id);
          }

          const listener = (frame: EventFrame) => {
            if (frameHasSessionActivity(frame)) {
              scheduleRefreshSessionsForGateway(gw.id);
            }
          };

          client.on('event', listener);
          clients.set(gw.id, client);
          listeners.set(gw.id, listener);
        }
      } else if (listeners.has(gw.id)) {
        cleanupGateway(gw.id);
      }
    }

    for (const gatewayId of Array.from(listeners.keys())) {
      if (!gateways.some((gw) => gw.id === gatewayId)) {
        cleanupGateway(gatewayId);
      }
    }
  }, [gateways, connectionStates, getClient, scheduleRefreshSessionsForGateway]);

  // ── Cleanup websocket activity listeners on unmount ──
  useEffect(() => {
    return () => {
      for (const { timer } of activityRefreshStatesRef.current.values()) {
        clearTimeout(timer);
      }
      activityRefreshStatesRef.current.clear();
      sessionSnapshotSequencesRef.current.clear();
      fullFetchSequencesRef.current.clear();
      pendingCreatedSessionKeysRef.current.clear();

      for (const [gatewayId, listener] of activityListenersRef.current) {
        activityClientsRef.current.get(gatewayId)?.off('event', listener);
      }
      activityClientsRef.current.clear();
      activityListenersRef.current.clear();
    };
  }, []);

  // ── Clean up data for removed gateways ──
  useEffect(() => {
    const gatewayIds = new Set(gateways.map(g => g.id));
    setSessionDataMap(prev => {
      let changed = false;
      const next = new Map(prev);
      for (const key of next.keys()) {
        if (!gatewayIds.has(key)) {
          next.delete(key);
          fetchedRef.current.delete(key);
          invalidateGatewaySequence(sessionSnapshotSequencesRef.current, key);
          invalidateGatewaySequence(fullFetchSequencesRef.current, key);
          pendingCreatedSessionKeysRef.current.delete(key);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [gateways]);

  // ── Refresh all ──
  const refreshAll = useCallback(async () => {
    const promises: Promise<void>[] = [];
    for (const gw of gateways) {
      const status = connectionStates.get(gw.id);
      if (status === 'connected') {
        promises.push(fetchSessionData(gw.id));
      }
    }
    await Promise.allSettled(promises);
  }, [gateways, connectionStates, fetchSessionData]);

  // ── Active session data ──
  const activeSessionData = useMemo(
    () => (activeGatewayId ? sessionDataMap.get(activeGatewayId) ?? null : null),
    [activeGatewayId, sessionDataMap],
  );

  // ── Active session key ──
  const activeSessionKey = useMemo(() => {
    if (!activeGatewayId) return getInitialSessionKey();
    return sessionKeys.get(activeGatewayId) ?? getInitialSessionKey();
  }, [activeGatewayId, sessionKeys]);

  // Ensure the focused gateway always points at a real or sensible fallback session.
  useEffect(() => {
    if (!activeGatewayId || !activeSessionData || activeSessionData.sessions.length === 0) {
      return;
    }

    const currentKey = sessionKeys.get(activeGatewayId) ?? getInitialSessionKey();
    const pendingCreatedKey = pendingCreatedSessionKeysRef.current.get(activeGatewayId);
    if (pendingCreatedKey && pendingCreatedKey === currentKey) {
      return;
    }

    const hasCurrentSession = activeSessionData.sessions.some((session) => session.key === currentKey);
    if (hasCurrentSession) {
      return;
    }

    const fallbackKey = chooseFallbackSessionKey(
      activeSessionData.sessions,
      activeSessionData.defaultAgentId,
      currentKey,
    );
    if (!fallbackKey || fallbackKey === currentKey) {
      return;
    }

    setSessionKeys((prev) => {
      const next = new Map(prev);
      next.set(activeGatewayId, fallbackKey);
      return next;
    });
    onSessionSwitch?.(activeGatewayId, fallbackKey);
  }, [activeGatewayId, activeSessionData, onSessionSwitch, sessionKeys]);

  // ── Sessions grouped by agent ──
  const sessionsByAgent = useMemo(() => {
    const groups = new Map<string, SessionRow[]>();
    if (!activeSessionData) return groups;

    for (const session of activeSessionData.sessions) {
      // Parse agent ID from session key (format: "agent:<agentId>:<rest>")
      const parts = session.key.split(':');
      const agentId = parts.length >= 2 && parts[0] === 'agent' ? parts[1] : 'other';
      const existing = groups.get(agentId) ?? [];
      existing.push(session);
      groups.set(agentId, existing);
    }

    // Add empty groups for agents with no sessions
    for (const agent of activeSessionData.agents) {
      if (!groups.has(agent.id)) {
        groups.set(agent.id, []);
      }
    }

    return groups;
  }, [activeSessionData]);

  // ── Derived values ──
  const agents = activeSessionData?.agents ?? [];
  const models = activeSessionData?.models ?? [];
  const defaults = activeSessionData?.defaults ?? null;
  const loading = activeSessionData?.loading ?? false;

  // ── Switch session ──
  const switchSession = useCallback((sessionKey: string) => {
    if (!activeGatewayId) return;

    setSessionKeys(prev => {
      const next = new Map(prev);
      next.set(activeGatewayId, sessionKey);
      return next;
    });

    onSessionSwitch?.(activeGatewayId, sessionKey);
  }, [activeGatewayId, onSessionSwitch]);

  // ── Patch session ──
  const patchSessionConfig = useCallback(async (patchParams: SessionPatchParams): Promise<boolean> => {
    if (!activeGatewayId) return false;

    const client = getClient(activeGatewayId);
    if (!client) return false;

    try {
      const result = await patchSession(client, patchParams);
      if (result.ok) {
        // Refresh session data after successful patch
        await fetchSessionData(activeGatewayId);
        return true;
      }
      return false;
    } catch (err) {
      console.error('[SessionManager] Patch failed:', err);
      return false;
    }
  }, [activeGatewayId, getClient, fetchSessionData]);

  // ── Create session ──
  const createSessionData = useCallback(async (params: SessionCreateParams): Promise<string | null> => {
    if (!activeGatewayId) return null;

    const client = getClient(activeGatewayId);
    if (!client) return null;

    try {
      const result = await createSession(client, params);
      if (!result.ok) {
        return null;
      }

      pendingCreatedSessionKeysRef.current.set(activeGatewayId, result.key);
      setSessionKeys((prev) => {
        const next = new Map(prev);
        next.set(activeGatewayId, result.key);
        return next;
      });
      onSessionSwitch?.(activeGatewayId, result.key);
      await refreshSessionsForGateway(activeGatewayId);
      return result.key;
    } catch (err) {
      console.error('[SessionManager] Create failed:', err);
      return null;
    }
  }, [activeGatewayId, getClient, onSessionSwitch, refreshSessionsForGateway]);

  // ── Delete session ──
  const deleteSessionData = useCallback(async (key: string): Promise<boolean> => {
    if (!activeGatewayId) return false;

    const client = getClient(activeGatewayId);
    if (!client) return false;

    try {
      const result = await deleteSession(client, key);
      if (!result.ok || !result.deleted) {
        return false;
      }

      const nextSessions = (activeSessionData?.sessions ?? []).filter((session) => session.key !== key);
      if (activeSessionKey === key) {
        const fallbackKey = choosePostDeleteFallbackSessionKey(
          nextSessions,
          activeSessionData?.defaultAgentId ?? 'main',
          key,
        );
        if (fallbackKey) {
          setSessionKeys((prev) => {
            const next = new Map(prev);
            next.set(activeGatewayId, fallbackKey);
            return next;
          });
          onSessionSwitch?.(activeGatewayId, fallbackKey);
        }
      }

      await refreshSessionsForGateway(activeGatewayId);
      return true;
    } catch (err) {
      console.error('[SessionManager] Delete failed:', err);
      return false;
    }
  }, [activeGatewayId, activeSessionData, activeSessionKey, getClient, onSessionSwitch, refreshSessionsForGateway]);

  // ── Patch agent model ──
  const patchAgentModel = useCallback(async (agentId: string, model: string | null): Promise<boolean> => {
    if (!activeGatewayId) return false;

    const client = getClient(activeGatewayId);
    if (!client) return false;

    try {
      // Try agents.patch first
      const result = await patchAgent(client, { agentId, model });
      if (result.ok) {
        await fetchSessionData(activeGatewayId);
        return true;
      }
      return false;
    } catch (err) {
      // If agents.patch is not supported, fall back to config.patch
      const errMsg = err instanceof Error ? err.message : String(err);
      if (errMsg.includes('not found') || errMsg.includes('unknown method') || errMsg.includes('Method not found')) {
        console.warn('[SessionManager] agents.patch not supported, falling back to config.patch');
        try {
          await client.request('config.patch', {
            path: `agents.${agentId}.model`,
            value: model,
          });
          await fetchSessionData(activeGatewayId);
          return true;
        } catch (fallbackErr) {
          console.error('[SessionManager] config.patch fallback failed:', fallbackErr);
          return false;
        }
      }
      console.error('[SessionManager] Agent patch failed:', err);
      return false;
    }
  }, [activeGatewayId, getClient, fetchSessionData]);

  // ── Reset session ──
  const resetSessionData = useCallback(async (key: string): Promise<boolean> => {
    if (!activeGatewayId) return false;

    const client = getClient(activeGatewayId);
    if (!client) return false;

    try {
      const result = await resetSession(client, key);
      if (result.ok) {
        // Refresh session data after reset
        await fetchSessionData(activeGatewayId);
        return true;
      }
      return false;
    } catch (err) {
      console.error('[SessionManager] Reset failed:', err);
      return false;
    }
  }, [activeGatewayId, getClient, fetchSessionData]);

  // ── Find session by key ──
  const findSession = useCallback((key: string): SessionRow | undefined => {
    return activeSessionData?.sessions.find(s => s.key === key);
  }, [activeSessionData]);

  return {
    activeSessionData,
    allSessionData: sessionDataMap,
    activeSessionKey,
    sessionsByAgent,
    agents,
    models,
    defaults,
    loading,
    fetchSessionData,
    refreshSessionsForGateway,
    refreshAll,
    switchSession,
    patchSessionConfig,
    createSessionData,
    deleteSessionData,
    patchAgentModel,
    resetSessionData,
    findSession,
  };
}
